import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import api from '../utils/api';

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [message, setMessage] = useState('');
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return; // guard against React.StrictMode double-invoke consuming the single-use token
    startedRef.current = true;
    const run = async () => {
      if (!token) {
        setStatus('error');
        setMessage('This verification link is missing its token.');
        return;
      }
      try {
        const { data } = await api.post('/auth/verify-email', { token });
        setStatus('success');
        setMessage(data.message || 'Your email has been verified.');
      } catch (err) {
        setStatus('error');
        const detail = err.response?.data?.detail;
        setMessage(typeof detail === 'string' ? detail : 'Verification failed. The link may be invalid or expired.');
      }
    };
    run();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))] px-4">
      <div className="w-full max-w-md bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-2xl shadow-lg p-8 text-center" data-testid="verify-email-page">
        {status === 'verifying' && (
          <div data-testid="verify-loading">
            <Loader2 className="w-14 h-14 text-[hsl(var(--primary))] mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold mb-2">Verifying your email…</h1>
            <p className="text-[hsl(var(--muted-foreground))] text-sm">Hang tight for a moment.</p>
          </div>
        )}
        {status === 'success' && (
          <div data-testid="verify-success">
            <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Email verified</h1>
            <p className="text-[hsl(var(--muted-foreground))] text-sm mb-6">{message}</p>
            <Link to="/login" className="inline-block bg-[hsl(var(--primary))] text-white px-6 py-2.5 rounded-lg font-semibold hover:opacity-90" data-testid="verify-go-login">
              Continue to sign in
            </Link>
          </div>
        )}
        {status === 'error' && (
          <div data-testid="verify-error">
            <AlertTriangle className="w-14 h-14 text-amber-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Verification failed</h1>
            <p className="text-[hsl(var(--muted-foreground))] text-sm mb-6">{message}</p>
            <Link to="/login" className="text-[hsl(var(--primary))] font-medium hover:underline" data-testid="verify-back-login">
              Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
